<script setup lang="ts">
import { DocumentNode } from 'graphql'
import { BarMitzvahType, TorahColumnOverlayType } from 'types/types'
import { computed, reactive, ref, watch } from 'vue'

import DeleteTorahColumnOverlay from '@/mutations/DeleteTorahColumnOverlay.graphql'
import DeleteTorahColumnOverlays from '@/mutations/DeleteTorahColumnOverlays.graphql'
import RenameTorahColumnOverlay from '@/mutations/RenameTorahColumnOverlay.graphql'
import ReorderTorahColumnOverlays from '@/mutations/ReorderTorahColumnOverlays.graphql'
import store from '@/store'
import { ReportingMutation, runReportedMutation } from '@/store/mutationHelpers'

interface Props {
  visible: boolean
  barMitzvah: BarMitzvahType | null
  overlays: TorahColumnOverlayType[]
}

const props = defineProps<Props>()

const emit = defineEmits<{
  close: []
  changed: []
}>()

const selected = ref<Set<string>>(new Set())

const state = reactive({
  dragFromIndex: null as number | null,
  editingId: null as string | null,
  editingName: '',
  localOverlays: [] as TorahColumnOverlayType[],
  saving: false,
})

const allSelected = computed(() => state.localOverlays.length > 0 && selected.value.size === state.localOverlays.length)
const someSelected = computed(() => selected.value.size > 0 && selected.value.size < state.localOverlays.length)

function toggleSelectAll() {
  if (allSelected.value) {
    selected.value = new Set()
  } else {
    selected.value = new Set(state.localOverlays.map((o) => o.id))
  }
}

function toggleSelect(id: string) {
  const next = new Set(selected.value)
  if (next.has(id)) {
    next.delete(id)
  } else {
    next.add(id)
  }
  selected.value = next
}

async function executeMutation<K extends ReportingMutation>(
  mutation: DocumentNode,
  variables: Record<string, unknown>,
  dataKey: K,
  errorTitle: string,
): Promise<boolean> {
  state.saving = true
  try {
    const payload = await runReportedMutation(mutation, variables, dataKey, (message) =>
      store.dispatch.dialog.show({ message, title: errorTitle }),
    )
    return !!payload
  } finally {
    state.saving = false
  }
}

watch(
  () => props.visible,
  (v) => {
    if (v) {
      state.localOverlays = [...props.overlays]
      state.editingId = null
      state.editingName = ''
      state.dragFromIndex = null
      selected.value = new Set()
    }
  },
)

function startEdit(overlay: TorahColumnOverlayType) {
  state.editingId = overlay.id
  state.editingName = overlay.name
}

function cancelEdit() {
  state.editingId = null
  state.editingName = ''
}

async function saveRename(overlay: TorahColumnOverlayType) {
  const name = state.editingName.trim()
  if (!name) {
    return
  }
  const ok = await executeMutation(RenameTorahColumnOverlay, { id: overlay.id, name }, 'renameTorahColumnOverlay', 'Rename Failed')
  if (ok) {
    const i = state.localOverlays.findIndex((o) => o.id === overlay.id)
    if (i !== -1) {
      state.localOverlays[i] = { ...state.localOverlays[i], name }
    }
    state.editingId = null
    state.editingName = ''
    emit('changed')
  }
}

function confirmDelete(overlay: TorahColumnOverlayType) {
  store.dispatch.dialog.show({
    acceptColor: 'error',
    acceptText: 'Delete',
    isRejectable: true,
    message: `Delete "${overlay.name}"? This cannot be undone.`,
    onAccept: () => doDelete(overlay),
    title: 'Delete column?',
  })
}

async function doDelete(overlay: TorahColumnOverlayType) {
  const ok = await executeMutation(DeleteTorahColumnOverlay, { id: overlay.id }, 'deleteTorahColumnOverlay', 'Delete Failed')
  if (ok) {
    state.localOverlays = state.localOverlays.filter((o) => o.id !== overlay.id)
    selected.value.delete(overlay.id)
    emit('changed')
  }
}

function confirmDeleteSelected() {
  const count = selected.value.size
  store.dispatch.dialog.show({
    acceptColor: 'error',
    acceptText: `Delete ${count}`,
    isRejectable: true,
    message: `Delete ${count} column${count === 1 ? '' : 's'}? This cannot be undone.`,
    onAccept: () => doDeleteSelected(),
    title: 'Delete columns?',
  })
}

async function doDeleteSelected() {
  const ids = [...selected.value]
  const ok = await executeMutation(DeleteTorahColumnOverlays, { ids }, 'deleteTorahColumnOverlays', 'Delete Failed')
  if (ok) {
    const deletedIds = new Set(ids)
    state.localOverlays = state.localOverlays.filter((o) => !deletedIds.has(o.id))
    selected.value = new Set()
    emit('changed')
  }
}

function onDragStart(index: number) {
  state.dragFromIndex = index
}

function onDrop(toIndex: number) {
  if (state.dragFromIndex === null || state.dragFromIndex === toIndex) {
    state.dragFromIndex = null
    return
  }
  const from = state.dragFromIndex
  const moved = state.localOverlays.splice(from, 1)[0]
  state.localOverlays.splice(toIndex, 0, moved)
  state.dragFromIndex = null
  saveOrder()
}

async function saveOrder() {
  if (!props.barMitzvah) {
    return
  }
  const ok = await executeMutation(
    ReorderTorahColumnOverlays,
    { barMitzvah: props.barMitzvah.student.username, orderedIds: state.localOverlays.map((o) => o.id) },
    'reorderTorahColumnOverlays',
    'Reorder Failed',
  )
  if (ok) {
    emit('changed')
  }
}

function onDialogUpdate(v: boolean) {
  if (!v) {
    emit('close')
  }
}
</script>

<template>
  <v-dialog max-width="500" :model-value="visible" @update:model-value="onDialogUpdate">
    <v-card>
      <v-card-title>Manage Columns</v-card-title>
      <v-card-text>
        <v-list>
          <v-list-item v-if="state.localOverlays.length > 1" density="compact">
            <template #prepend>
              <v-checkbox-btn :indeterminate="someSelected" :model-value="allSelected" @update:model-value="toggleSelectAll" />
            </template>
            <v-list-item-title class="text-caption text-medium-emphasis">Select all</v-list-item-title>
            <template #append>
              <template v-if="selected.size">
                <span class="text-body-2 mr-2">{{ selected.size }} selected</span>
                <v-btn
                  color="error"
                  :disabled="state.saving"
                  icon="mdi-delete"
                  size="small"
                  variant="text"
                  @click="confirmDeleteSelected"
                />
              </template>
            </template>
          </v-list-item>
          <v-list-item
            v-for="(overlay, index) in state.localOverlays"
            :key="overlay.id"
            :draggable="state.editingId === null"
            @dragover.prevent
            @dragstart="onDragStart(index)"
            @drop.prevent="onDrop(index)"
          >
            <template #prepend>
              <v-checkbox-btn
                v-if="state.localOverlays.length > 1"
                :model-value="selected.has(overlay.id)"
                @update:model-value="toggleSelect(overlay.id)"
              />
              <v-icon v-if="state.localOverlays.length > 1" class="drag-handle" icon="mdi-drag" style="cursor: grab" />
            </template>

            <template v-if="state.editingId === overlay.id">
              <v-text-field
                v-model="state.editingName"
                autofocus
                density="compact"
                hide-details
                variant="underlined"
                @keydown.enter="saveRename(overlay)"
                @keydown.esc="cancelEdit"
              />
            </template>
            <template v-else>
              <v-list-item-title>{{ overlay.name }}</v-list-item-title>
            </template>

            <template #append>
              <template v-if="state.editingId === overlay.id">
                <v-btn
                  :disabled="state.saving || !state.editingName.trim()"
                  icon="mdi-check"
                  size="small"
                  variant="text"
                  @click="saveRename(overlay)"
                />
                <v-btn :disabled="state.saving" icon="mdi-close" size="small" variant="text" @click="cancelEdit" />
              </template>
              <template v-else>
                <v-btn :disabled="state.saving" icon="mdi-pencil" size="small" variant="text" @click="startEdit(overlay)" />
                <v-btn :disabled="state.saving" icon="mdi-delete" size="small" variant="text" @click="confirmDelete(overlay)" />
              </template>
            </template>
          </v-list-item>
        </v-list>
        <p v-if="state.localOverlays.length === 0" class="text-medium-emphasis">No columns.</p>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn variant="plain" @click="emit('close')">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
